import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { FileText, Download, Shield, Mail, Bell, Loader2, AlertTriangle, Building2, User, ChevronDown } from "lucide-react";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era una copia locale).
import { formatDate } from "../lib/format";

const todayIso = () => new Date().toISOString().slice(0, 10);

function annualBadge(p) {
  const tone = (cls) => `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`;
  if (p.renewed === true || p.status === "renewed") {
    return <span className={tone("bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400")}>Rinnovato</span>;
  }
  const expired = p.expiry_date && String(p.expiry_date).slice(0, 10) < todayIso();
  if (p.status === "not_renewed" || (expired && p.renewed !== true)) {
    return <span className={tone("bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400")}>Non rinnovato</span>;
  }
  return <span className={tone("bg-muted text-muted-foreground")}>Attiva</span>;
}

function semiBadge(p) {
  if (!p.semiannual_expiry) return null;
  const tone = (cls) => `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`;
  if (p.semiannual_paid === true) {
    return <span className={tone("bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400")}>Pagato</span>;
  }
  return <span className={tone("bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400")}>Non pagato</span>;
}

export default function PortalePage() {
  const { token } = useParams();
  const [me, setMe] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notif, setNotif] = useState({ email: "", enabled: false, days_before: 30 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(null);

  useEffect(() => {
    load();
  }, [token]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [meR, polR, docR, notifR] = await Promise.all([
        axios.get(`/api/portal/${token}/me`),
        axios.get(`/api/portal/${token}/policies`),
        axios.get(`/api/portal/${token}/documents`),
        axios.get(`/api/portal/${token}/notifications`),
      ]);
      setMe(meR.data);
      setPolicies(polR.data || []);
      setDocuments(docR.data || []);
      setNotif(notifR.data || { email: "", enabled: false, days_before: 30 });
    } catch (e) {
      setError(e.response?.status === 404 ? "not_found" : "generic");
    } finally {
      setLoading(false);
    }
  };

  const saveNotif = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`/api/portal/${token}/notifications`, {
        email: notif.email,
        enabled: notif.enabled,
      });
      setNotif(res.data);
      toast.success("Preferenze salvate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const downloadDocument = async (documentId) => {
    try {
      const res = await axios.get(`/api/portal/${token}/documents/${documentId}/download`, {
        responseType: "blob",
      });
      const ct = res.headers["content-type"] || "";
      if (ct.includes("application/json")) {
        const text = await res.data.text();
        const data = JSON.parse(text);
        if (data.redirect) {
          window.open(data.redirect, "_blank");
          return;
        }
        throw new Error("File non disponibile");
      }
      const blob = res.data;
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      const name = m ? m[1] : `documento-${documentId}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nel download del documento");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-10">
            <AlertTriangle className="h-12 w-12 mx-auto text-orange-500 mb-4" />
            <h1 className="text-lg font-bold">Link non valido o disattivato</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Il link non è più attivo. Contatta il tuo broker per riceverne uno nuovo.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-10">
            <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h1 className="text-lg font-bold">Errore</h1>
            <p className="text-sm text-muted-foreground mt-2">Impossibile caricare il portale.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-border p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center flex-shrink-0">
              {me?.client_type === "business" ? (
                <Building2 className="h-6 w-6 text-primary" />
              ) : (
                <User className="h-6 w-6 text-primary" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold font-heading">{me?.name}</h1>
              <p className="text-sm text-muted-foreground">AssiGlobal — Area riservata</p>
            </div>
          </div>
        </div>

        {/* Polizze */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Le tue polizze
            </CardTitle>
          </CardHeader>
          <CardContent>
            {policies.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Nessuna polizza attiva.</p>
            ) : (
              <div className="divide-y divide-border">
                {policies.map((p) => {
                  const policyDocs = documents.filter((d) => d.policy_id === p.policy_id);
                  const isOpen = openPolicy === p.policy_id;
                  return (
                    <div key={p.policy_id || p.policy_number} className="py-3">
                      <div
                        className="flex items-start justify-between gap-3 cursor-pointer select-none"
                        onClick={() => setOpenPolicy(isOpen ? null : p.policy_id)}
                      >
                        <div className="flex items-start gap-2 min-w-0">
                          <ChevronDown
                            className={`h-4 w-4 mt-1 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                          <div className="min-w-0">
                            <p className="font-medium">{p.policy_number || "N/A"}</p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {p.company} · {p.policy_type}
                              {p.license_plate && <span> · <span className="font-mono">{p.license_plate}</span></span>}
                            </p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {annualBadge(p)}
                              {semiBadge(p)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-muted-foreground">Scade</p>
                          <p className="font-semibold">{formatDate(p.expiry_date, "-")}</p>
                          {p.semiannual_expiry && (
                            <>
                              <p className="text-xs text-muted-foreground mt-1">Rata semestrale</p>
                              <p className="text-sm font-medium">{formatDate(p.semiannual_expiry, "-")}</p>
                            </>
                          )}
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-3 ml-6 border-l-2 border-primary/20 pl-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5" /> Documenti della polizza
                          </p>
                          {policyDocs.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">Nessun documento per questa polizza.</p>
                          ) : (
                            <div className="space-y-1">
                              {policyDocs.map((d) => (
                                <div key={d.document_id} className="flex items-center justify-between gap-3 py-1.5">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">{d.name}</p>
                                    <p className="text-xs text-muted-foreground">{formatDate(d.created_at, "-")}</p>
                                  </div>
                                  <Button size="sm" variant="outline" onClick={() => downloadDocument(d.document_id)}>
                                    <Download className="h-3.5 w-3.5 mr-1.5" /> Scarica
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documenti personali (livello cliente) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Documenti personali
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documents.filter((d) => d.entity_type === "client").length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Nessun documento disponibile.</p>
            ) : (
              <div className="divide-y divide-border">
                {documents.filter((d) => d.entity_type === "client").map((d) => (
                  <div key={d.document_id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(d.created_at, "-")}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadDocument(d.document_id)}>
                      <Download className="h-3.5 w-3.5 mr-1.5" /> Scarica
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Avvisi scadenze */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" /> Avvisi scadenze
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Ricevi un promemoria via email <strong>{notif.days_before} giorni prima</strong> della scadenza
              di una polizza.
            </p>
            <div className="space-y-2">
              <Label htmlFor="portal-notif-email">Email per gli avvisi</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input
                  id="portal-notif-email"
                  type="email"
                  value={notif.email}
                  onChange={(e) => setNotif({ ...notif, email: e.target.value })}
                  placeholder="nome@azienda.it"
                />
              </div>
            </div>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={notif.enabled}
                onChange={(e) => setNotif({ ...notif, enabled: e.target.checked })}
                className="h-4 w-4"
              />
              Avvisami quando una polizza è in scadenza
            </label>
            <Button onClick={saveNotif} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salva preferenze
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
